import axiosInstance from '@/services'
import { defineStore } from 'pinia'
import { useGlobalStore } from './global'

export const useLookupsStore = defineStore('lookups', {
  state: () => {
    return {
      optionSourceMap: {},
      countries: null,
      emirates: null,
      transactionReasons: null,
      professions: null,
      genders: null,
      passportTypes: null,
      maritalStatuses: null,
      educationLevels: null,
      religions: null,
      nameDetails: null,
      goldenVisatransactionReasons: [],
      activeCenters: []
    }
  },
  getters: {
    countriesAsPhoneCodes() {
      if (!this.countries) return []

      return this.countries.map((c) => {
        return {
          ...c,
          label: `${c.diallingCode} - ${c.localizedName}`,
          value: c.diallingCode
        }
      })
    }
  },
  actions: {
    async fetchLookup(
      path,
      params,
      headers,
      needProcess = false,
      method = 'GET',
      data = {},
      isNeedCache = true
    ) {
      let res = []

      const storagePath = `${path}?${new URLSearchParams(params).toString()}`

      const cachedData = sessionStorage.getItem(storagePath)

      const noCachePaths = [
        '/api/lookup/GetTransactionReasonsByModuleParentReasonId',
        '/api/lookup/GetFilteredRefundTransactionReasons',
        '/api/lookup/yearsOfEstablishmentCard',
        '/api/lookup/moduleParentService',
        '/api/lookup/transactions',
        '/api/lookup/modules',
        '/api/lookup/services',
        '/api/lookup/fileStatus',
        '/api/lookup/sortableColumns',
        '/api/lookup/departments'
      ]

      if (noCachePaths.includes(path) === false) {
        if (isNeedCache && cachedData) {
          const parsedData = JSON.parse(cachedData)
          if (parsedData?.length > 0) return parsedData
        }
      }
      if (method == 'GET') {
        res = await axiosInstance.get(path, {
          params,
          headers: {
            languageId: useGlobalStore().languageId || 2,
            ...headers
          }
        })
      } else {
        res = await axiosInstance.post(path, data, {
          params,
          headers: {
            languageId: useGlobalStore().languageId || 2,
            ...headers
          }
        })
      }

      if (!Array.isArray(res)) {
        res = res?.[params?.data_set] || res.data
      }

      let parsedData = []
      if (!needProcess) {
        parsedData = res?.map((e) => {
          return {
            ...e,
            label: e.localizedName,
            value: +e.code
          }
        })
      } else {
        parsedData = res
      }

      /**
       * needed in review step (to not call the dropdown apis again)
       */
      // if (!noCachePaths.includes(path)) {
      //   sessionStorage.setItem(storagePath, JSON.stringify(parsedData))
      // }

      return parsedData
    },
    // export selector options to lookup store.
    async overrideOptionsSource(key, value) {
      this.optionSourceMap[key] = value
    },
    async getCountries() {
      this.countries = await this.fetchLookup('/api/lookup/countries')
    },
    async getShipTypes() {
      this.shipTypes = await this.fetchLookup('/api/lookup/shipTypes')
    },
    async getEmirates() {
      this.emirates = await this.fetchLookup('/api/lookup/emirates')
    },
    async getTransactionReasons() {
      const globalStore = useGlobalStore()
      this.transactionReasons = await this.fetchLookup('/api/lookup/transactionReason', {
        serviceTransactionId: globalStore.startedService.transactionId
      })
    },
    async getProfessions() {
      const globalStore = useGlobalStore()
      const path = '/api/lookup/professionsAutoComplete'

      const cachedData = sessionStorage.getItem(path)
      if (cachedData) {
        this.professions = JSON.parse(cachedData)
        return
      }

      const data = await axiosInstance.post(
        path,
        {
          serviceTransactionId: +globalStore.startedService.transactionId
        },
        {
          headers: {
            languageId: 2
          }
        }
      )

      this.professions = data.map((e) => {
        return {
          ...e,
          label: e.localizedName,
          value: e.id
        }
      })

      sessionStorage.setItem(path, JSON.stringify(this.professions))
    },
    async getGenders() {
      this.genders = await this.fetchLookup('/api/lookup/genders')
    },
    async getDropdowns() {
      await this.getCountries()
      await this.getEmirates()
      await this.getTransactionReasons()
      // await this.getProfessions()
      await this.getGenders()

      this.passportTypes = await this.fetchLookup('/api/lookup/passportTypes')
      this.religions = await this.fetchLookup('/api/lookup/religions')
      this.maritalStatuses = await this.fetchLookup('/api/lookup/maritalStatuses')
      this.educationLevels = await this.fetchLookup('/api/lookup/educationLevels')
    },
    async getServiceNameDetails() {
      return await axiosInstance.get('/api/service/nameDetails')
    },

    async getTransactionReasonsByModuleParentReasonId(
      serviceTransactionId,
      moduleParentReasonId = null
    ) {
      const path = '/api/lookup/GetTransactionReasonsByModuleParentReasonId'
      const params = { serviceTransactionId }
      if (moduleParentReasonId) {
        Object.assign(params, { moduleParentReasonId })
      }
      return await axiosInstance.get(path, { params })
    },

    async getGoldenVisatransactionReasons(serviceTransactionId, transactionReasonCategoryId) {
      const sixMonthGVReasons = [479, 481, 488, 485, 749, 750, 900, 901, 1069, 1070]
      if (!sixMonthGVReasons.includes(transactionReasonCategoryId)) return

      const reasons = await this.getTransactionReasonsByModuleParentReasonId(
        serviceTransactionId,
        transactionReasonCategoryId
      )

      return reasons
    }
  }
})
